const API_BASE_URL = (process.env.REACT_APP_CLIPS_API_URL || "").replace(
  /\/+$/,
  "",
);

const DEFAULT_TIMEOUT_MS = 30_000;
const UPLOAD_TIMEOUT_MS = 20 * 60_000;
const IMPORT_TIMEOUT_MS = 15 * 60_000;

const EXPORT_POLL_INTERVAL_MS = 2_000;
const EXPORT_POLL_TIMEOUT_MS = 10 * 60_000;

export class ClipApiError extends Error {
  constructor(message, { status = 0, code = "request_failed", details } = {}) {
    super(message);

    this.name = "ClipApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getToken() {
  return localStorage.getItem("token")?.trim() || "";
}

function getErrorMessage(payload, fallback) {
  if (typeof payload === "string" && payload.trim()) {
    return payload.trim();
  }

  if (payload?.error) {
    return payload.error;
  }

  if (payload?.message) {
    return payload.message;
  }

  return fallback;
}

async function request(
  path,
  {
    method = "GET",
    body,
    headers = {},
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = {},
) {
  const token = getToken();

  if (!token) {
    throw new ClipApiError("Your session has expired. Please sign in again.", {
      status: 401,
      code: "missing_token",
    });
  }

  const controller = new AbortController();

  const abortFromCaller = () => {
    controller.abort(signal?.reason);
  };

  if (signal?.aborted) {
    abortFromCaller();
  } else {
    signal?.addEventListener("abort", abortFromCaller, {
      once: true,
    });
  }

  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      body,
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        ...headers,
      },
    });

    const responseText = await response.text();

    let payload = null;

    if (responseText) {
      try {
        payload = JSON.parse(responseText);
      } catch {
        payload = responseText;
      }
    }

    if (!response.ok) {
      throw new ClipApiError(
        getErrorMessage(
          payload,
          `Request failed with status ${response.status}`,
        ),
        {
          status: response.status,
          code: payload?.code || "api_error",
          details: payload,
        },
      );
    }

    return payload;
  } catch (error) {
    if (error instanceof ClipApiError) {
      throw error;
    }

    if (controller.signal.aborted) {
      const cancelledByCaller = Boolean(signal?.aborted);

      throw new ClipApiError(
        cancelledByCaller
          ? "The request was cancelled."
          : "The request took too long. Please try again.",
        {
          code: cancelledByCaller ? "request_cancelled" : "request_timeout",
        },
      );
    }

    throw new ClipApiError(
      "VlogSlicer could not reach the server. Check your connection and try again.",
      {
        code: "network_error",
        details: error,
      },
    );
  } finally {
    window.clearTimeout(timeout);

    signal?.removeEventListener("abort", abortFromCaller);
  }
}

function normalizeStatus(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  if (["done", "analysis_completed"].includes(normalized)) {
    return "completed";
  }

  if (normalized === "analysis_failed") {
    return "failed";
  }

  if (normalized === "analysis_queued") {
    return "queued";
  }

  if (["processing", "analysis_running"].includes(normalized)) {
    return "running";
  }

  return normalized || "queued";
}

export function normalizeJob(job = {}) {
  return {
    ...job,

    id: job.id || job.job_id,

    job_id: job.job_id || job.id,

    video_id: Number(job.video_id || job.video?.id || 0),

    progress: Number(job.progress || 0),

    status: normalizeStatus(job.status),

    message: job.message || "",

    job_type: job.job_type || job.type || "analysis",
  };
}

function asAssetObject(value) {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
}

function normalizeSuggestion(suggestion = {}) {
  const scores = suggestion.scores || {};

  const previewAsset = asAssetObject(suggestion.preview);
  const thumbnailAsset = asAssetObject(suggestion.thumbnail);
  const downloadAsset = asAssetObject(suggestion.download);

  const rank = Number(suggestion.rank || suggestion.clip_index || 0);

  const startSeconds = Number(
    suggestion.start_seconds ?? suggestion.start_sec ?? suggestion.start ?? 0,
  );

  const endSeconds = Number(
    suggestion.end_seconds ?? suggestion.end_sec ?? suggestion.end ?? 0,
  );

  const previewURL =
    previewAsset.url ||
    suggestion.preview_url ||
    suggestion.preview_signed_url ||
    suggestion.signed_preview_url ||
    suggestion.video_url ||
    suggestion.video_signed_url ||
    suggestion.signed_video_url ||
    suggestion.clip_url ||
    suggestion.signed_clip_url ||
    suggestion.asset_url ||
    suggestion.signed_url ||
    suggestion.url ||
    "";

  const thumbnailURL =
    thumbnailAsset.url ||
    suggestion.thumbnail_url ||
    suggestion.thumbnail_signed_url ||
    suggestion.signed_thumbnail_url ||
    suggestion.preview_thumbnail_url ||
    suggestion.poster_url ||
    suggestion.poster ||
    (typeof suggestion.thumbnail === "string" ? suggestion.thumbnail : "") ||
    "";

  const downloadURL =
    downloadAsset.url ||
    suggestion.download_url ||
    suggestion.download_signed_url ||
    suggestion.signed_download_url ||
    suggestion.export_url ||
    suggestion.signed_export_url ||
    suggestion.file_url ||
    (typeof suggestion.download === "string" ? suggestion.download : "") ||
    "";

  const downloadFilename =
    downloadAsset.filename ||
    suggestion.download_filename ||
    `vlogslicer-clip-${rank || 1}.mp4`;

  const durationSeconds = Number(
    suggestion.duration_seconds ??
      suggestion.duration ??
      previewAsset.duration_seconds ??
      Math.max(0, endSeconds - startSeconds),
  );

  const score = Number(
    suggestion.score_total ?? scores.total ?? suggestion.score ?? 0,
  );

  return {
    ...suggestion,

    id: suggestion.id || suggestion.clip_id || suggestion.rank,

    clip_id: suggestion.clip_id || suggestion.id || suggestion.rank,

    rank,

    title: suggestion.title || "Untitled clip",

    subtitle:
      suggestion.subtitle || suggestion.description || suggestion.reason || "",

    start_seconds: startSeconds,

    start_sec: startSeconds,

    end_seconds: endSeconds,

    end_sec: endSeconds,

    duration_seconds: durationSeconds,

    score_total: score,

    score,

    preview: {
      ...previewAsset,
      url: previewURL,
    },

    thumbnail: {
      ...thumbnailAsset,
      url: thumbnailURL,
    },

    download: {
      ...downloadAsset,
      url: downloadURL,
      filename: downloadFilename,
    },

    preview_url: previewURL,

    video_url: previewURL,

    url: previewURL,

    thumbnail_url: thumbnailURL,

    download_url: downloadURL,

    download_filename: downloadFilename,
  };
}

export function uploadVideo(file, { signal } = {}) {
  const formData = new FormData();

  formData.append("file", file);

  return request("/api/clip/videos/upload", {
    method: "POST",
    body: formData,
    signal,
    timeoutMs: UPLOAD_TIMEOUT_MS,
  });
}

export function importVideo(videoURL, { signal } = {}) {
  return request("/api/clip/videos/from-url", {
    method: "POST",

    body: JSON.stringify({
      url: videoURL,
    }),

    headers: {
      "Content-Type": "application/json",
    },

    signal,

    timeoutMs: IMPORT_TIMEOUT_MS,
  });
}

export function startAnalysis(videoId, { signal } = {}) {
  return request(`/api/clip/videos/${encodeURIComponent(videoId)}/analyze`, {
    method: "POST",

    headers: {
      "Content-Type": "application/json",
    },

    signal,
  });
}

export async function retrimClip(
  suggestionId,
  startSeconds,
  endSeconds,
  { signal } = {},
) {
  const normalizedSuggestionId = String(suggestionId || "").trim();

  if (!normalizedSuggestionId) {
    throw new ClipApiError("Clip suggestion ID is required.", {
      code: "missing_suggestion_id",
    });
  }

  const normalizedStartSeconds = Number(startSeconds);
  const normalizedEndSeconds = Number(endSeconds);

  if (
    !Number.isFinite(normalizedStartSeconds) ||
    !Number.isFinite(normalizedEndSeconds)
  ) {
    throw new ClipApiError("Clip start and end must be valid numbers.", {
      code: "invalid_retrim_range",
    });
  }

  const payload = await request(
    `/api/clip/suggestions/${encodeURIComponent(normalizedSuggestionId)}/retrim`,
    {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({
        start_seconds: normalizedStartSeconds,
        end_seconds: normalizedEndSeconds,
      }),

      signal,
    },
  );

  return {
    ...payload,

    export_id: payload?.export_id || payload?.export?.id || "",

    suggestion_id:
      payload?.suggestion_id ||
      payload?.export?.suggestion_id ||
      normalizedSuggestionId,

    status: normalizeStatus(payload?.status || payload?.export?.status),

    progress: Number(payload?.progress || payload?.export?.progress || 0),
  };
}

export async function getAnalysisStatus(jobId, { signal } = {}) {
  const payload = await request(`/api/clip/jobs/${encodeURIComponent(jobId)}`, {
    signal,
  });

  const job = normalizeJob(payload?.job || payload);

  const suggestions =
    payload?.suggestions ||
    payload?.clips ||
    payload?.job?.suggestions ||
    payload?.job?.clips ||
    payload?.results?.suggestions ||
    payload?.results?.clips ||
    [];

  return {
    ...payload,

    job,

    suggestions: Array.isArray(suggestions)
      ? suggestions.map(normalizeSuggestion)
      : [],
  };
}

function normalizeClipExport(clipExport = {}) {
  const previewAsset = asAssetObject(clipExport.preview);
  const thumbnailAsset = asAssetObject(clipExport.thumbnail);

  const previewURL = previewAsset.url || clipExport.preview_url || "";

  const thumbnailURL = thumbnailAsset.url || clipExport.thumbnail_url || "";

  const startSeconds = Number(clipExport.start_seconds ?? 0);
  const endSeconds = Number(clipExport.end_seconds ?? 0);

  return {
    ...clipExport,

    id: clipExport.id || clipExport.export_id || "",

    export_id: clipExport.export_id || clipExport.id || "",

    suggestion_id: clipExport.suggestion_id || "",

    video_id: Number(clipExport.video_id || 0),

    start_seconds: startSeconds,

    end_seconds: endSeconds,

    duration_seconds: Number(
      clipExport.duration_seconds ??
        previewAsset.duration_seconds ??
        Math.max(0, endSeconds - startSeconds),
    ),

    status: normalizeStatus(clipExport.status),

    progress: Number(clipExport.progress || 0),

    message: clipExport.message || "",

    last_failed_stage: clipExport.last_failed_stage || "",

    preview: {
      ...previewAsset,
      url: previewURL,
    },

    thumbnail: {
      ...thumbnailAsset,
      url: thumbnailURL,
    },

    preview_url: previewURL,

    thumbnail_url: thumbnailURL,
  };
}

export async function getClipExportStatus(exportId, { signal } = {}) {
  const normalizedExportId = String(exportId || "").trim();

  if (!normalizedExportId) {
    throw new ClipApiError("Clip export ID is required.", {
      code: "missing_export_id",
    });
  }

  const payload = await request(
    `/api/clip/exports/${encodeURIComponent(normalizedExportId)}`,
    {
      signal,
    },
  );

  const clipExport = normalizeClipExport(payload?.export || payload);

  return {
    ...payload,

    export: clipExport,
  };
}

function waitForPollInterval(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(
        new ClipApiError("The request was cancelled.", {
          code: "request_cancelled",
        }),
      );
      return;
    }

    const timeout = window.setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, ms);

    function handleAbort() {
      window.clearTimeout(timeout);

      reject(
        new ClipApiError("The request was cancelled.", {
          code: "request_cancelled",
        }),
      );
    }

    signal?.addEventListener("abort", handleAbort, {
      once: true,
    });
  });
}

export async function pollClipExportStatus(
  exportId,
  {
    signal,
    intervalMs = EXPORT_POLL_INTERVAL_MS,
    timeoutMs = EXPORT_POLL_TIMEOUT_MS,
    onUpdate,
  } = {},
) {
  const normalizedExportId = String(exportId || "").trim();

  if (!normalizedExportId) {
    throw new ClipApiError("Clip export ID is required.", {
      code: "missing_export_id",
    });
  }

  const startedAt = Date.now();

  while (true) {
    if (signal?.aborted) {
      throw new ClipApiError("The request was cancelled.", {
        code: "request_cancelled",
      });
    }

    const payload = await getClipExportStatus(normalizedExportId, {
      signal,
    });

    const clipExport = payload?.export || {};

    if (typeof onUpdate === "function") {
      onUpdate(clipExport);
    }

    if (clipExport.status === "completed") {
      return clipExport;
    }

    if (clipExport.status === "failed") {
      return clipExport;
    }

    if (clipExport.status !== "queued" && clipExport.status !== "running") {
      throw new ClipApiError(
        `Unexpected clip export status: ${clipExport.status || "unknown"}`,
        {
          code: "unexpected_export_status",
          details: clipExport,
        },
      );
    }

    if (Date.now() - startedAt >= timeoutMs) {
      throw new ClipApiError(
        "Clip processing is taking longer than expected. Please try again.",
        {
          code: "export_poll_timeout",
          details: clipExport,
        },
      );
    }

    await waitForPollInterval(intervalMs, signal);
  }
}

export function getClipPreviewURL(clip) {
  return (
    clip?.preview?.url ||
    clip?.preview_url ||
    clip?.video_url ||
    clip?.url ||
    ""
  );
}

export function getClipThumbnailURL(clip) {
  return (
    clip?.thumbnail?.url ||
    clip?.thumbnail_url ||
    (typeof clip?.thumbnail === "string" ? clip.thumbnail : "") ||
    ""
  );
}

export function getClipDownloadURL(clip) {
  return (
    clip?.download?.url ||
    clip?.download_url ||
    clip?.download_signed_url ||
    clip?.signed_download_url ||
    (typeof clip?.download === "string" ? clip.download : "") ||
    ""
  );
}

export function getClipDownloadFilename(clip) {
  const rank = Number(clip?.rank) || 1;

  return (
    clip?.download?.filename ||
    clip?.download_filename ||
    `vlogslicer-clip-${rank}.mp4`
  );
}

export function downloadClip(clip) {
  const downloadURL = getClipDownloadURL(clip);

  if (!downloadURL) {
    throw new ClipApiError(
      "This clip does not have a downloadable video yet.",
      {
        code: "missing_download_url",
      },
    );
  }

  const anchor = document.createElement("a");

  anchor.href = downloadURL;
  anchor.rel = "noopener noreferrer";
  anchor.style.display = "none";

  document.body.appendChild(anchor);

  try {
    anchor.click();
  } finally {
    anchor.remove();
  }
}

export async function listAnalysisJobs({ signal } = {}) {
  const payload = await request("/api/clip/jobs", {
    signal,
  });

  return {
    ...payload,

    jobs: Array.isArray(payload?.jobs) ? payload.jobs.map(normalizeJob) : [],
  };
}
